import {
  ConfirmBetFailed,
  ConfirmBetslipBetsSucceeded,
  ConfirmBetSucceeded,
  ProposeBetFailed,
  ProposeBetSucceeded,
  QuickbetClosed,
  QuickbetLoadSelection,
  SetInvalidInvestmentNotification,
  ProposeMultiBetSucceeded,
  ProposeMultiBetFailed,
  ConfirmMultiBetSucceeded,
  ConfirmMultiBetFailed,
} from '@core/Areas/Quickbet/signals'
import { QuickbetState, state$ as quickbetState$ } from '@core/Areas/Quickbet/driver'
import {
  InvestmentStateRecord,
  state$ as investmentState$,
} from '@core/Areas/Quickbet/Components/BetInvestment/driver'
import {
  AnalyticsBetData,
  AnalyticsData,
  defaultData as analyticsDefaultData,
  keys as analyticsKeys,
} from '@classic/Foundation/Analytics/AnalyticsDataLayer'
import * as Analytics from '@classic/Foundation/Analytics/Analytics'
import { trackBetConfirmation } from '@classic/Foundation/Analytics/Analytics.Bet'
import { ensureBetErrorTypeLiteral } from '@core/Data/betting'
import { getFeatures } from '@core/State/FeatureFlags/Store/selectors'
import { store } from '@core/Store'
import { betslipAnalyticsSubscriber } from './helpers/betslipAnalyticsSubscriber'

type AnalyticsBetAndQuickbetData = Pick<AnalyticsData, 'bet' | 'quickbet'>

const extractErrorMessage = (betErrorType: unknown): Nullable<string> =>
  typeof betErrorType === 'string' || typeof betErrorType === 'number'
    ? ensureBetErrorTypeLiteral(betErrorType)
    : null

const defaultBetData = analyticsDefaultData.bet
const defaultQuickbetData = analyticsDefaultData.quickbet

export function register(): { dispose(): void } {
  let superPickErrorMessage: string | null = null

  // Subscribing to SuperPick error state to store message in variable for use later
  const setInvalidInvestmentNotificationSubscription =
    SetInvalidInvestmentNotification.signal$.subscribe(x => {
      if (x.data.type === 'InvalidSuperPickSelection') {
        superPickErrorMessage = 'SuperPick Error'
      }
    })

  const quickbetLoadSelectionSubscription = QuickbetLoadSelection.signal$.subscribe(() => {
    superPickErrorMessage = null
    const opened = { timestamp: new Date() }
    Analytics.track(analyticsKeys.quickbetOpen, { quickbet: { opened } })
  })

  const quickbetClosedSubscription = QuickbetClosed.signal$
    .withLatestFrom(quickbetState$, (_, quickbetState) => ({ quickbetState }))
    .subscribe(x => {
      const closed = {
        betPlaced: x.quickbetState.betPlaced,
        timestamp: new Date(),
      }
      Analytics.track(analyticsKeys.quickbetClosed, { quickbet: { closed } })
    })

  // -------------------- Bet step 1 --------------------
  const proposeBetFailedSubscription = ProposeBetFailed.signal$
    .withLatestFrom(investmentState$, (proposeBetFailed, investmentState) => ({
      proposeBetFailed,
      investmentState,
    }))
    .subscribe(x => {
      const investmentState: InvestmentStateRecord = { ...x.investmentState.toJS() }
      const keypadState = investmentState[investmentState.activeInvestment].lastKeyPressed.mode

      const errorMessage =
        extractErrorMessage(x.proposeBetFailed.data.response.type) ??
        x.proposeBetFailed.data.response.message

      const dataToTrack: AnalyticsBetAndQuickbetData = {
        bet: { ...defaultBetData, errorMessage, construction: 'quickbet' },
        quickbet: { ...defaultQuickbetData, keypadState },
      }
      Analytics.track(analyticsKeys.betAccountSelected, dataToTrack)
    })

  const proposeBetSucceededSubscription = ProposeBetSucceeded.signal$
    .withLatestFrom(investmentState$, (proposeBetSucceeded, investmentState) => ({
      proposeBetSucceeded,
      investmentState,
    }))
    .subscribe(x => {
      const investmentState: InvestmentStateRecord = { ...x.investmentState.toJS() }
      const keypadState = investmentState[investmentState.activeInvestment].lastKeyPressed.mode

      const dataToTrack: AnalyticsBetAndQuickbetData = {
        bet: { ...defaultBetData, errorMessage: superPickErrorMessage, construction: 'quickbet' },
        quickbet: { ...defaultQuickbetData, keypadState },
      }
      Analytics.track(analyticsKeys.betAccountSelected, dataToTrack)
    })

  // -------------------- Bet step 2 --------------------
  const confirmBetFailedSubscription = ConfirmBetFailed.signal$.subscribe(x => {
    const errorMessage = extractErrorMessage(x.data.response.type) ?? x.data.response.message
    const newBetData: AnalyticsBetData = {
      ...defaultBetData,
      errorMessage,
      construction: 'quickbet',
    }
    Analytics.track(analyticsKeys.betAccountReview, { bet: newBetData })
  })

  // -------------------- Bet step 2 & 3 --------------------

  const confirmBetSucceededSubscription = ConfirmBetSucceeded.signal$
    .withLatestFrom(quickbetState$, (confirmBetSucceeded, quickbetState) => ({
      confirmBetSucceeded,
      quickbetState,
    }))
    .subscribe(x => {
      // Step 2: Update data layer with reset bet data
      const newBetDataStep2: AnalyticsBetData = { ...defaultBetData, construction: 'quickbet' }
      Analytics.track(analyticsKeys.betAccountReview, { bet: newBetDataStep2 })

      // Step 3: Update data layer with new bet information
      const { selectionDetails, selection, betSource, betSourceShareOrigin } =
        x.quickbetState.toJS() as QuickbetState

      const { ticketNumber, winInvestment, placeInvestment, betCost, specialOffers, bonusBet } =
        x.confirmBetSucceeded.data

      const features = getFeatures(store.getState())

      trackBetConfirmation({
        ticketNumber,
        selection,
        selectionDetails,
        specialOffers,
        betConstruction: 'quickbet',
        betCost,
        winInvestment,
        placeInvestment,
        features,
        betSource,
        betSourceShareOrigin,
        bonusCashAmount: bonusBet ?? 0,
      })
    })

  // BetSlip bets being placed via Quickbet (FavNums etc)
  const confirmBetslipBetsSucceededSubscription = ConfirmBetslipBetsSucceeded.signal$
    .withLatestFrom(quickbetState$, (confirmAllBetsSuccessful, quickbetState) => {
      const getSelection: BetslipAnalyticsSubscriberParams['getSelection'] = () => {
        const quickbetStateData = quickbetState.toJS() as QuickbetState
        return !quickbetStateData.selection || !quickbetStateData.selectionDetails
          ? null
          : [quickbetStateData.selection, quickbetStateData.selectionDetails]
      }
      return {
        confirmAllBetsSuccessful,
        getSelection,
        construction: 'quickbet',
      } as BetslipAnalyticsSubscriberParams
    })
    .subscribe(betslipAnalyticsSubscriber)

  const proposeMultiBetFailedSubscription = ProposeMultiBetFailed.signal$
    .withLatestFrom(investmentState$, (proposeMultiBetFailed, investmentState) => ({
      proposeMultiBetFailed,
      investmentState,
    }))
    .subscribe(x => {
      const investmentState: InvestmentStateRecord = { ...x.investmentState.toJS() }
      const keypadState = investmentState[investmentState.activeInvestment]?.lastKeyPressed?.mode

      const errorMessage =
        extractErrorMessage(x.proposeMultiBetFailed.data.response.type) ??
        x.proposeMultiBetFailed.data.response.message

      const dataToTrack: AnalyticsBetAndQuickbetData = {
        bet: { ...defaultBetData, errorMessage, construction: 'quickbet' },
        quickbet: { ...defaultQuickbetData, keypadState },
      }

      Analytics.track(analyticsKeys.betAccountSelected, dataToTrack)
    })

  const proposeMultiBetSucceededSubscription = ProposeMultiBetSucceeded.signal$
    .withLatestFrom(investmentState$, (proposeMultiBetSucceeded, investmentState) => ({
      proposeMultiBetSucceeded,
      investmentState,
    }))
    .subscribe(x => {
      const investmentState: InvestmentStateRecord = { ...x.investmentState.toJS() }
      const keypadState = investmentState[investmentState.activeInvestment]?.lastKeyPressed?.mode

      const dataToTrack: AnalyticsBetAndQuickbetData = {
        bet: { ...defaultBetData, construction: 'quickbet' },
        quickbet: { ...defaultQuickbetData, keypadState },
      }

      Analytics.track(analyticsKeys.betAccountSelected, dataToTrack)
    })

  const confirmMultiBetFailedSubscription = ConfirmMultiBetFailed.signal$.subscribe(x => {
    const errorMessage = extractErrorMessage(x.data.code) ?? x.data.response.message

    const newBetData: AnalyticsBetData = {
      ...defaultBetData,
      errorMessage,
      construction: 'quickbet',
    }

    Analytics.track(analyticsKeys.betAccountReview, { bet: newBetData })
  })

  const confirmMultiBetSucceededSubscription = ConfirmMultiBetSucceeded.signal$
    .withLatestFrom(quickbetState$, (confirmAllBetsSuccessful, quickbetState) => {
      const quickbetStateData = quickbetState.toJS() as QuickbetState
      const getSelection: BetslipAnalyticsSubscriberParams['getSelection'] = () => {
        return !quickbetStateData.selection || !quickbetStateData.selectionDetails
          ? null
          : [quickbetStateData.selection, quickbetStateData.selectionDetails]
      }
      const data: BetslipAnalyticsSubscriberParams = {
        confirmAllBetsSuccessful,
        getSelection,
        construction: 'quickbet',
        betSource: quickbetStateData.betSource || undefined,
        betSourceShareOrigin: quickbetStateData.betSourceShareOrigin || undefined,
      }
      return data
    })
    .subscribe(betslipAnalyticsSubscriber)

  return {
    dispose() {
      setInvalidInvestmentNotificationSubscription.dispose()
      quickbetLoadSelectionSubscription.dispose()
      quickbetClosedSubscription.dispose()
      proposeBetFailedSubscription.dispose()
      proposeBetSucceededSubscription.dispose()
      confirmBetFailedSubscription.dispose()
      confirmBetSucceededSubscription.dispose()
      confirmBetslipBetsSucceededSubscription.dispose()
      proposeMultiBetFailedSubscription.dispose()
      proposeMultiBetSucceededSubscription.dispose()
      confirmMultiBetFailedSubscription.dispose()
      confirmMultiBetSucceededSubscription.dispose()
    },
  }
}

// Types

type BetslipAnalyticsSubscriberParams = Parameters<typeof betslipAnalyticsSubscriber>[0]
